<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold" v-text="'KYC Approved Users'"></h3>
              <v-spacer></v-spacer>
              <v-chip class="ma-2" color="success" text-color="white" large>
                <v-avatar left>
                  <v-icon>mdi-check-circle</v-icon>
                </v-avatar>
                Total Approved: {{ count }}
              </v-chip>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search users..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    ref="position"
                    v-model="filter.position_id"
                    :items="positions"
                    label="Position"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-account"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    ref="status"
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-toggle-switch"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="loading" style="min-height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table v-else fixed-header class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left sr_no">Sr No</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">User Details</th>
                      <th class="text-left">Email</th>
                      <th class="text-left">Position</th>
                      <th class="text-left">Store</th>
                      <th class="text-left">Driving License Details</th>
                      <th class="text-left">Aadhar Number</th>
                      <th class="text-left">GST Certificate</th>
                      <th class="text-left">Address</th>
                      <th class="text-left">KYC Status</th>
                      <th class="text-left">Is Point</th>
                      <th class="text-left">Is Gift</th>
                      <th class="text-left">Is KYC Bonus</th>
                      <th class="text-left">Is Welcome Bonus</th>
                      <th class="text-left">Total Bonus</th>
                      <th class="text-left">Joined Date</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="data.length === 0 && !loading">
                    <tr>
                      <td colspan="19" class="text-center pa-4">
                        <div class="text-h6 text--secondary">
                          <v-icon size="48" color="grey lighten-2">
                            mdi-account-off
                          </v-icon>
                          <div class="mt-2">No KYC approved users found</div>
                          <div class="text-caption">
                            Users will appear here once their KYC documentation
                            is verified and approved.
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr
                      v-for="(item, index) in data"
                      :key="item.id"
                      :class="
                        index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
                      "
                    >
                      <td class="sr_no">
                        {{ (page - 1) * rowsPerPage + index + 1 }}
                      </td>
                      <td>
                        <v-tooltip bottom v-if="item.is_active == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="teal"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Active
                            </v-chip>
                          </template>
                          <span>User is Active</span>
                        </v-tooltip>
                        <v-tooltip bottom v-else>
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-close-circle</v-icon>
                              </v-avatar>
                              Inactive
                            </v-chip>
                          </template>
                          <span>User is Inactive</span>
                        </v-tooltip>
                      </td>
                      <td>
                        <div class="d-flex align-center">
                          <v-avatar size="32" class="mr-2">
                            <img
                              v-if="item.image_path"
                              :src="item.image_path"
                              :alt="item.user_name"
                            />
                            <v-icon v-else>mdi-account</v-icon>
                          </v-avatar>
                          <div>
                            <div class="font-weight-medium">
                              {{
                                item.user_name ||
                                item.first_name + " " + (item.last_name || "")
                              }}
                            </div>
                            <div class="text-caption text--secondary">
                              {{ item.phone }}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{{ item.email || "N/A" }}</td>
                      <td>
                        <v-chip
                          small
                          :color="
                            getPositionColor(
                              item.position && item.position.name
                            )
                          "
                          text-color="white"
                        >
                          {{ (item.position && item.position.name) || "N/A" }}
                        </v-chip>
                      </td>
                      <td>
                        <span v-if="item.store">{{ item.store.name }}</span>
                        <span v-else class="text--secondary">Not Assigned</span>
                      </td>
                      <td style="min-width: 280px">
                        <div
                          v-if="
                            item.driving_license &&
                            item.driving_license_details &&
                            item.driving_license_details
                              .license_number_formatted !== 'Not Available'
                          "
                          class="license-details-compact"
                        >
                          <div class="d-flex align-center mb-1">
                            <v-icon small color="primary" class="mr-1"
                              >mdi-card-account-details</v-icon
                            >
                            <strong class="primary--text">{{
                              item.driving_license_details
                                .license_number_formatted
                            }}</strong>
                            <v-chip
                              x-small
                              :color="
                                item.driving_license_details.license_status
                                  .color
                              "
                              text-color="white"
                              class="ml-2"
                            >
                              {{
                                item.driving_license_details.license_status
                                  .status
                              }}
                            </v-chip>
                          </div>
                          <div class="license-info-compact text-caption">
                            <div>
                              <strong>Name:</strong>
                              {{ item.driving_license_details.holder_name }}
                            </div>
                            <div>
                              <strong>DOB:</strong>
                              {{
                                item.driving_license_details
                                  .date_of_birth_formatted
                              }}
                            </div>
                            <div>
                              <strong>Expiry:</strong>
                              {{
                                item.driving_license_details
                                  .license_expiry_date_formatted
                              }}
                            </div>
                            <div>
                              <strong>State:</strong>
                              {{ item.driving_license_details.issuing_state }}
                            </div>
                          </div>
                        </div>
                        <div v-else class="text--secondary text-center">
                          <v-icon small class="mr-1">mdi-alert-circle</v-icon>
                          No Details
                        </div>
                      </td>
                      <td style="min-width: 200px">
                        <div v-if="item.aadhar_card" class="aadhar-details">
                          <div class="d-flex align-center mb-1">
                            <v-icon small color="orange" class="mr-1"
                              >mdi-card-account-details-outline</v-icon
                            >
                            <strong class="orange--text">{{
                              item.aadhar_card
                            }}</strong>
                          </div>
                          <div class="text-caption">
                            <div>
                              <strong>Name:</strong>
                              {{
                                item.user_name ||
                                item.first_name + " " + (item.last_name || "")
                              }}
                            </div>
                          </div>
                        </div>
                        <div v-else class="text--secondary text-center">
                          <v-icon small class="mr-1">mdi-alert-circle</v-icon>
                          No Details
                        </div>
                      </td>
                      <td style="min-width: 200px">
                        <div v-if="item.gst_certificate" class="gst-details">
                          <div class="d-flex align-center mb-1">
                            <v-icon small color="green" class="mr-1">
                              mdi-certificate
                            </v-icon>
                            <strong class="green--text">
                              {{ item.gst_certificate }}
                            </strong>
                          </div>
                        </div>
                        <div v-else class="text--secondary text-center">
                          <v-icon small class="mr-1">mdi-alert-circle</v-icon>
                          No Details
                        </div>
                      </td>
                      <td style="min-width: 200px">
                        <div v-if="item.address" class="address-details">
                          <div class="d-flex align-center mb-1">
                            <span>
                              {{ item.address }}
                            </span>
                          </div>
                        </div>
                        <div v-else class="text--secondary text-center">
                          <v-icon small class="mr-1">mdi-alert-circle</v-icon>
                          No Address
                        </div>
                      </td>
                      <td>
                        <v-chip small color="success" text-color="white">
                          <v-avatar left>
                            <v-icon size="15">mdi-check-circle</v-icon>
                          </v-avatar>
                          KYC Approved
                        </v-chip>
                      </td>
                      <td>{{ item.is_point == 1 ? "YES" : "NO" }}</td>
                      <td>{{ item.is_gift == 1 ? "YES" : "NO" }}</td>
                      <td>{{ item.is_kyc_bonus == 1 ? "YES" : "NO" }}</td>
                      <td>{{ item.is_welcome_bonus == 1 ? "YES" : "NO" }}</td>
                      <td>{{ item.welcome_bonus || "0" }}</td>
                      <td style="min-width: 130px">
                        {{ formatDate(item.created_at) }}
                      </td>
                      <td class="text-right action_th">
                        <v-tooltip
                          bottom
                          v-if="
                            item.driving_license &&
                            item.driving_license_details &&
                            item.driving_license_details
                              .license_number_formatted !== 'Not Available'
                          "
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="my-auto"
                              color="primary"
                              @click="viewLicenseDetails(item)"
                            >
                              <v-icon dark>mdi-card-account-details</v-icon>
                            </v-btn>
                          </template>
                          <span>View Complete License Details</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              :class="
                                item.driving_license &&
                                item.driving_license_details &&
                                item.driving_license_details
                                  .license_number_formatted !== 'Not Available'
                                  ? 'ml-2'
                                  : ''
                              "
                              fab
                              x-small
                              color="info"
                              @click="viewUser(item)"
                            >
                              <v-icon dark>mdi-eye</v-icon>
                            </v-btn>
                          </template>
                          <span>View User Profile</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Pagination -->
              <v-row class="mt-4" v-if="count > rowsPerPage">
                <v-col cols="12">
                  <v-pagination
                    v-model="page"
                    :length="Math.ceil(count / rowsPerPage)"
                    :total-visible="7"
                    @input="getData"
                  ></v-pagination>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Driving License Details Dialog -->
    <v-dialog v-model="licenseDetailsDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left dark>mdi-card-account-details</v-icon>
          Complete Driving License Details
          <v-spacer></v-spacer>
          <v-btn icon dark @click="licenseDetailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text
          class="pa-6"
          v-if="selectedUser && selectedUser.driving_license_details"
        >
          <v-row>
            <!-- Basic License Information -->
            <v-col cols="12" md="6">
              <v-card outlined class="mb-4">
                <v-card-subtitle class="primary--text font-weight-bold">
                  <v-icon color="primary" class="mr-2">mdi-card-text</v-icon>
                  License Information
                </v-card-subtitle>
                <v-card-text>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-medium"
                          >License Number</v-list-item-title
                        >
                        <v-list-item-subtitle class="text-h6 primary--text">
                          {{
                            selectedUser.driving_license_details
                              .license_number_formatted
                          }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>License Type</v-list-item-title>
                        <v-list-item-subtitle>{{
                          selectedUser.driving_license_details.license_type
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Issuing State</v-list-item-title>
                        <v-list-item-subtitle>{{
                          selectedUser.driving_license_details.issuing_state
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Status</v-list-item-title>
                        <v-list-item-subtitle>
                          <v-chip
                            small
                            :color="
                              selectedUser.driving_license_details
                                .license_status.color
                            "
                            text-color="white"
                          >
                            {{
                              selectedUser.driving_license_details
                                .license_status.status
                            }}
                          </v-chip>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Personal Information -->
            <v-col cols="12" md="6">
              <v-card outlined class="mb-4">
                <v-card-subtitle class="success--text font-weight-bold">
                  <v-icon color="success" class="mr-2">mdi-account</v-icon>
                  Personal Information
                </v-card-subtitle>
                <v-card-text>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Full Name</v-list-item-title>
                        <v-list-item-subtitle class="font-weight-medium">{{
                          selectedUser.driving_license_details.holder_name
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Father's Name</v-list-item-title>
                        <v-list-item-subtitle>{{
                          selectedUser.driving_license_details.father_name
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Date of Birth</v-list-item-title>
                        <v-list-item-subtitle>{{
                          selectedUser.driving_license_details
                            .date_of_birth_formatted
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Validity Information -->
            <v-col cols="12" md="6">
              <v-card outlined class="mb-4">
                <v-card-subtitle class="warning--text font-weight-bold">
                  <v-icon color="warning" class="mr-2">mdi-calendar</v-icon>
                  Validity Information
                </v-card-subtitle>
                <v-card-text>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title
                          >License Expiry Date</v-list-item-title
                        >
                        <v-list-item-subtitle>{{
                          selectedUser.driving_license_details
                            .license_expiry_date_formatted
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                      v-if="
                        selectedUser.driving_license_details
                          .transport_license_expiry !== 'Not Available'
                      "
                    >
                      <v-list-item-content>
                        <v-list-item-title
                          >Transport License Expiry</v-list-item-title
                        >
                        <v-list-item-subtitle>{{
                          selectedUser.driving_license_details
                            .transport_license_expiry_formatted
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Address Information -->
            <v-col cols="12" md="6">
              <v-card outlined class="mb-4">
                <v-card-subtitle class="info--text font-weight-bold">
                  <v-icon color="info" class="mr-2">mdi-map-marker</v-icon>
                  Address Information
                </v-card-subtitle>
                <v-card-text>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Address</v-list-item-title>
                        <v-list-item-subtitle
                          style="white-space: normal; word-wrap: break-word"
                          >{{
                            selectedUser.driving_license_details.address
                          }}</v-list-item-subtitle
                        >
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>City</v-list-item-title>
                        <v-list-item-subtitle>{{
                          selectedUser.driving_license_details.city
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>State</v-list-item-title>
                        <v-list-item-subtitle>{{
                          selectedUser.driving_license_details.state
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>PIN Code</v-list-item-title>
                        <v-list-item-subtitle>{{
                          selectedUser.driving_license_details.pin_code
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="licenseDetailsDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "KycApprovedUsers",
  data() {
    return {
      loading: false,
      data: [],
      count: 0,
      page: 1,
      rowsPerPage: 10,
      positions: [],
      statuses: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      filter: {
        search_keyword: null,
        position_id: null,
        is_active: null,
      },
      // License details dialog
      licenseDetailsDialog: false,
      selectedUser: null,
    };
  },

  mounted() {
    this.getMasters();
    this.getData();
  },

  methods: {
    async getMasters() {
      try {
        const response = await axios.get("users/masters");
        if (response.data.positions) {
          this.positions = response.data.positions.map((position) => ({
            text: position.name,
            value: position.id,
          }));
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },

    async getData() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          rowsPerPage: this.rowsPerPage,
          ...this.filter,
        };

        // Remove null/undefined values
        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });

        const response = await axios.get("users/kyc-approved", { params });

        if (response.data.success) {
          this.data = response.data.data;
          this.count = response.data.count;
        }
      } catch (error) {
        console.error("Error fetching KYC approved users:", error);

        // Handle different error types
        let errorMessage = "Failed to fetch KYC approved users";
        if (error.response) {
          // Server responded with error status
          if (error.response.status === 401) {
            errorMessage = "Authentication failed. Please login again.";
          } else if (error.response.status === 403) {
            errorMessage = "You don't have permission to view KYC data.";
          } else if (error.response.status === 500) {
            errorMessage = "Server error. Please try again later.";
          }
        } else if (error.request) {
          // Network error
          errorMessage = "Network error. Please check your connection.";
        }

        this.$store.dispatch("errors/showError", {
          message: errorMessage,
        });
      } finally {
        this.loading = false;
      }
    },

    getPositionColor(position) {
      const colors = {
        ADMIN: "red",
        FRANCHISEE: "purple",
        "MARKETING EXECUTIVE": "blue",
        "SALES EXECUTIVE": "green",
        DRIVER: "orange",
        TRANSPORTER: "indigo",
      };
      return colors[position] || "grey";
    },

    getGSTTypeName(gstType) {
      const types = {
        0: "Unregistered/Consumer",
        1: "Registered-Regular",
        2: "Registered-Composite",
      };
      return types[gstType] || "Unknown";
    },

    getGSTTypeColor(gstType) {
      const colors = {
        0: "grey",
        1: "green",
        2: "blue",
      };
      return colors[gstType] || "grey";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },

    viewUser(user) {
      this.$router.push(`/users/${this.encryptIt(user.id)}`);
    },

    viewLicenseDetails(user) {
      this.selectedUser = user;
      this.licenseDetailsDialog = true;
    },
  },
};
</script>

<style scoped>
.license-details-compact {
  font-size: 0.75rem;
}

.license-info-compact {
  line-height: 1.3;
}

.license-info-compact > div {
  margin-bottom: 2px;
}
</style>